import { useState } from 'react'
import MFAccordion from '../../../../../../../components/ui-components/MFAccordion'
import type { PlanRecommendationPairView } from '../../pair/planRecommendationPair.types'
import { EventNames } from '../../../../../../../types/misc.types'
import { trackEvent } from '../../../../../../../utils/appBridge'
import { PlansLumpsumSheetEventIds, PlansScreen } from '../../../../analytics/plansEvents'
import {
  BuyReasonPointer,
  getBuyReasonSubtitleLabel
} from './BuyReasonPointers'

type ReasonsToBuyAccordionProps = {
  pair: PlanRecommendationPairView
  defaultExpanded?: boolean
  analyticsScreen?: string
}

export function ReasonsToBuyAccordion({
  pair,
  defaultExpanded = false,
  analyticsScreen = PlansScreen.LumpsumAllocationRecommendation
}: ReasonsToBuyAccordionProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)
  const safeBuyReasons = pair.buyReasons ?? []
  if (safeBuyReasons.length === 0) {
    return null
  }

  const buyFund = pair.enteringFund

  const subtitle = safeBuyReasons
    .map((reason) => getBuyReasonSubtitleLabel(reason))
    .filter(Boolean)
    .join(', ')

  return (
    <MFAccordion
      title="Reasons to Buy"
      iconComponent={<ReasonsToBuyAccordionIcon />}
      isExpanded={isExpanded}
      onAccordionClick={() => {
        const fundName = buyFund?.fundName ?? ''
        const fundIsin = buyFund?.isin ?? ''
        trackEvent({
          eventName: EventNames.UserClicked,
          payload: {
            ScreenName: analyticsScreen,
            eventId: PlansLumpsumSheetEventIds.ReasonsAccordion,
            buttonLabel: 'Reasons to Buy',
            ...(fundName ? { fundName } : {}),
            ...(fundIsin ? { fundIsin } : {})
          }
        })
        setIsExpanded((currentExpanded) => !currentExpanded)
      }}
      isBorderTop
      accordionHeaderClassName="!px-5"
      middleContent={(
        <div className="flex min-w-0 flex-col">
          <div className="text-[14px] font-[500] leading-[130%] text-[#232323]">Reasons to Buy</div>
          <div className="truncate text-[13px] font-[400] leading-[140%] text-[#6A6E6E]">
            {subtitle}
          </div>
        </div>
      )}
    >
      <div className="pb-4">
        <div className="flex flex-col divide-y divide-[#F0F4F7] [&>*]:py-4 [&>*:first-child]:pt-1">
          {safeBuyReasons.map((reason, reasonIndex) => (
            <BuyReasonPointer
              key={`${reason?.reason ?? 'buy'}-${reasonIndex}`}
              reason={reason}
              index={reasonIndex + 1}
              buyCategoryName={buyFund.categoryName}
              buyPowerRank={buyFund.powerRank}
              buyPowerRating={buyFund.powerRating}
              isBuyExecuted={buyFund.executed}
              categoryAllocationExecutionStatus={buyFund.categoryAllocationExecutionStatus}
              categoryFundCountExecutionStatus={buyFund.categoryFundCountExecutionStatus}
            />
          ))}
        </div>
      </div>
    </MFAccordion>
  )
}

export function ReasonsToBuyAccordionIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden>
      <path
        d="M11.2188 23.7143V28.1071C11.2188 28.9159 11.8743 29.5714 12.683 29.5714H27.3259C28.1346 29.5714 28.7902 28.9159 28.7902 28.1071V23.7143C28.7902 22.9056 28.1346 22.25 27.3259 22.25H12.683C11.8743 22.25 11.2188 22.9056 11.2188 23.7143Z"
        stroke="#3D3D3D"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M11.2188 12.4643V16.8571C11.2188 17.6658 11.8743 18.3214 12.683 18.3214H27.3259C28.1346 18.3214 28.7902 17.6658 28.7902 16.8571V12.4643C28.7902 11.6556 28.1346 11 27.3259 11H12.683C11.8743 11 11.2188 11.6556 11.2188 12.4643Z"
        stroke="#3D3D3D"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M22.9375 29.5714V22.25" stroke="#3D3D3D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M17.0781 18.3214V11" stroke="#3D3D3D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
